import React, { useState } from 'react';
import '../../styles/onboardingFlow/onboarding/onboardingInstallPlugin.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import OnboardingLogoPanel from '../onboardingComponents/OnboardingLogoPanel';
import OnboardingFooter from '../onboardingComponents/OnboardingFooter';
import {
  setAiOnboardingNextStep,
  setAiOnboardingStepCompleted,
} from '../../store/reducers/userSlice';
import update_all_shop_stats_api_call from '../../lib/api/dashboard/update_all_shop_stats_api_call';
import { asyncHandler } from '../../lib/api/async-handler';
import { RequestInfo } from '../../interfaces/RequestInfo';
import {
  ADUP_API_KEY_GENERATE_API,
  CENTRAL_API_BASE_URL,
} from '../../lib/constants/central-api';

type ServiceChoice = 'managed' | 'self' | null;

const OnboardingInstallPlugin: React.FC = () => {
  const dispatch = useDispatch();

  const accessToken = useSelector(
    (state: RootState) => state.auth.authorization.accessToken
  );
  const shopSlug = useSelector(
    (state: RootState) => state.user.company.shop_slug
  );

  const [serviceChoice, setServiceChoice] = useState<ServiceChoice>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isSavingPreference, setIsSavingPreference] = useState(false);
  const [managedConfirmed, setManagedConfirmed] = useState(false);

  async function generateApiKey() {
    setIsGenerating(true);
    try {
      const requestInfo: RequestInfo = {
        url: ADUP_API_KEY_GENERATE_API(shopSlug),
        method: 'POST',
        body: JSON.stringify({ name: 'Claude Code Plugin', read_scope: true, write_scope: false }),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json',
          Authorization: `${accessToken.type} ${accessToken.sellerToken}`,
        },
      };

      const res: any = await asyncHandler(
        requestInfo,
        'GENERATE_API_KEY',
        '',
        false,
        false,
        true
      );

      if (res?.data?.success && res?.data?.data?.key) {
        setApiKey(res.data.data.key);
      } else if (res?.data?.data?.api_key) {
        setApiKey(res.data.data.api_key);
      }
    } catch (error) {
      console.error('Error generating API key:', error);
    } finally {
      setIsGenerating(false);
    }
  }

  const handleCopy = async () => {
    if (!apiKey) return;
    try {
      await navigator.clipboard.writeText(apiKey);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = apiKey;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      // Simulate verification - in production this would check plugin connection status
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setIsVerified(true);
    } catch (error) {
      console.error('Verification failed:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSelectManaged = async () => {
    setServiceChoice('managed');
    setIsSavingPreference(true);
    try {
      await saveServicePreference('managed');
      setManagedConfirmed(true);
    } catch (error) {
      console.error('Error saving preference:', error);
    } finally {
      setIsSavingPreference(false);
    }
  };

  const handleSelectSelf = () => {
    setServiceChoice('self');
    generateApiKey();
  };

  const saveServicePreference = async (type: 'managed' | 'self') => {
    try {
      await update_all_shop_stats_api_call(3, false, {
        claude_plugin_service_type: type,
      });
    } catch (error) {
      console.error('Error saving service preference:', error);
    }
  };

  const handleContinue = async () => {
    try {
      if (serviceChoice === 'self') {
        await update_all_shop_stats_api_call(3, false, {
          claude_plugin_service_type: 'self',
        });
      }
      dispatch(
        setAiOnboardingStepCompleted({ stepNumber: 3, isCompleted: true })
      );
      dispatch(setAiOnboardingNextStep(4));
    } catch (error) {
      console.error('Error advancing step:', error);
    }
  };

  const handleSkip = async () => {
    try {
      dispatch(
        setAiOnboardingStepCompleted({ stepNumber: 3, isCompleted: true })
      );
      dispatch(setAiOnboardingNextStep(4));
    } catch (error) {
      console.error('Error skipping step:', error);
    }
  };

  const handleBackToChoice = () => {
    setServiceChoice(null);
    setApiKey(null);
    setIsVerified(false);
    setManagedConfirmed(false);
  };

  // Phase A: Service choice selection
  if (!serviceChoice) {
    return (
      <div className='onboardingInstallPlugin'>
        <OnboardingLogoPanel />
        <div className='onboardingInstallPlugin__header'>
          <div className='onboardingInstallPlugin__header-row'>
            <div className='onboardingInstallPlugin__header-icon'>
              <svg
                width='28'
                height='28'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z'
                  stroke='#D97706'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <polyline
                  points='3.27 6.96 12 12.01 20.73 6.96'
                  stroke='#D97706'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <line
                  x1='12'
                  y1='22.08'
                  x2='12'
                  y2='12'
                  stroke='#D97706'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
            <h1 className='onboardingInstallPlugin__title'>
              Connect with Claude
            </h1>
          </div>
          <p className='onboardingInstallPlugin__subtitle'>
            Your platforms are connected. Now let's activate AI-powered
            marketing analysis with Claude and TARA.
          </p>
        </div>

        <div className='onboardingInstallPlugin__choice'>
          <div
            className='onboardingInstallPlugin__choice-card onboardingInstallPlugin__choice-card--managed'
            onClick={handleSelectManaged}
          >
            <div className='onboardingInstallPlugin__choice-badge'>
              Recommended
            </div>
            <div className='onboardingInstallPlugin__choice-icon'>
              <svg
                width='32'
                height='32'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2'
                  stroke='#7C3AED'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <circle
                  cx='9'
                  cy='7'
                  r='4'
                  stroke='#7C3AED'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M23 21v-2a4 4 0 0 0-3-3.87'
                  stroke='#7C3AED'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M16 3.13a4 4 0 0 1 0 7.75'
                  stroke='#7C3AED'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
            <h3 className='onboardingInstallPlugin__choice-title'>
              Managed Service
            </h3>
            <p className='onboardingInstallPlugin__choice-description'>
              Our team handles the complete setup, plugin configuration, and
              ongoing optimization for your account.
            </p>
            <ul className='onboardingInstallPlugin__choice-features'>
              <li>Full plugin setup by our team</li>
              <li>Personalized configuration</li>
              <li>Ongoing optimization support</li>
              <li>Priority onboarding assistance</li>
            </ul>
            <div className='onboardingInstallPlugin__choice-stat'>
              <svg
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M13 2L3 14h9l-1 8 10-12h-9l1-8z'
                  stroke='#D97706'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <span>
                Marketing agencies become 30% more effective when using managed
                services
              </span>
            </div>
          </div>

          <div
            className='onboardingInstallPlugin__choice-card onboardingInstallPlugin__choice-card--self'
            onClick={handleSelectSelf}
          >
            <div className='onboardingInstallPlugin__choice-icon'>
              <svg
                width='32'
                height='32'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <polyline
                  points='16 18 22 12 16 6'
                  stroke='#667085'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <polyline
                  points='8 6 2 12 8 18'
                  stroke='#667085'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <line
                  x1='12'
                  y1='2'
                  x2='12'
                  y2='22'
                  stroke='#667085'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  transform='rotate(20 12 12)'
                />
              </svg>
            </div>
            <h3 className='onboardingInstallPlugin__choice-title'>
              Self-Setup
            </h3>
            <p className='onboardingInstallPlugin__choice-description'>
              Set up the Claude plugin yourself with your own API key. Best for
              teams with technical resources.
            </p>
            <ul className='onboardingInstallPlugin__choice-features'>
              <li>Instant API key generation</li>
              <li>Full control over configuration</li>
              <li>Step-by-step guided setup</li>
            </ul>
          </div>
        </div>

        <div className='onboardingInstallPlugin__footer'>
          <button
            type='button'
            className='onboardingInstallPlugin__skip'
            onClick={handleSkip}
          >
            Skip for now - I'll set this up later
          </button>
        </div>

        <OnboardingFooter />
      </div>
    );
  }

  // Phase B: Managed service confirmation
  if (serviceChoice === 'managed') {
    return (
      <div className='onboardingInstallPlugin'>
        <OnboardingLogoPanel />
        <div className='onboardingInstallPlugin__header'>
          <div className='onboardingInstallPlugin__header-row'>
            <div className='onboardingInstallPlugin__header-icon'>
              <svg
                width='28'
                height='28'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M22 11.08V12a10 10 0 1 1-5.93-9.14'
                  stroke='#10b981'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <polyline
                  points='22 4 12 14.01 9 11.01'
                  stroke='#10b981'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
            <h1 className='onboardingInstallPlugin__title'>
              You're All Set!
            </h1>
          </div>
          <p className='onboardingInstallPlugin__subtitle'>
            Great choice! Our team will handle the complete Claude plugin setup
            for your account.
          </p>
        </div>

        <div className='onboardingInstallPlugin__managed-confirmation'>
          <div className='onboardingInstallPlugin__managed-card'>
            <div className='onboardingInstallPlugin__managed-icon'>
              <svg
                width='48'
                height='48'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'
                  stroke='#7C3AED'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
            <h3 className='onboardingInstallPlugin__managed-title'>
              What happens next?
            </h3>
            <div className='onboardingInstallPlugin__managed-steps'>
              <div className='onboardingInstallPlugin__managed-step'>
                <div className='onboardingInstallPlugin__managed-step-number'>
                  1
                </div>
                <div>
                  <strong>We'll reach out within 24 hours</strong>
                  <p>
                    Our onboarding specialist will contact you to schedule a
                    setup session.
                  </p>
                </div>
              </div>
              <div className='onboardingInstallPlugin__managed-step'>
                <div className='onboardingInstallPlugin__managed-step-number'>
                  2
                </div>
                <div>
                  <strong>We configure your Claude plugin</strong>
                  <p>
                    Our team sets up the ADUP plugin in Claude Code, connects
                    your platforms, and optimizes everything.
                  </p>
                </div>
              </div>
              <div className='onboardingInstallPlugin__managed-step'>
                <div className='onboardingInstallPlugin__managed-step-number'>
                  3
                </div>
                <div>
                  <strong>You start getting AI-powered insights</strong>
                  <p>
                    Ask Claude anything about your marketing data and get
                    instant, data-driven answers via TARA.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='onboardingInstallPlugin__footer'>
          <MainSquareButton
            type='button'
            value={isSavingPreference ? 'Saving...' : 'Continue'}
            normal={true}
            loading={isSavingPreference}
            disabled={isSavingPreference}
            onClick={handleContinue}
            style={{
              width: '100%',
              paddingTop: '1.2rem',
              paddingBottom: '1.2rem',
            }}
          />
          <button
            type='button'
            className='onboardingInstallPlugin__skip'
            onClick={handleBackToChoice}
          >
            Go back and choose a different option
          </button>
        </div>

        <OnboardingFooter />
      </div>
    );
  }

  // Phase C: Self-setup
  return (
    <div className='onboardingInstallPlugin'>
      <OnboardingLogoPanel />
      <div className='onboardingInstallPlugin__header'>
        <div className='onboardingInstallPlugin__header-row'>
          <div className='onboardingInstallPlugin__header-icon'>
            <svg
              width='28'
              height='28'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <polyline
                points='16 18 22 12 16 6'
                stroke='#7C3AED'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <polyline
                points='8 6 2 12 8 18'
                stroke='#7C3AED'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <h1 className='onboardingInstallPlugin__title'>
            Set Up the Claude Plugin
          </h1>
        </div>
        <p className='onboardingInstallPlugin__subtitle'>
          Follow these steps to connect ADUP to Claude Code for AI-powered
          marketing analysis.
        </p>
      </div>

      <div className='onboardingInstallPlugin__steps'>
        {/* Step 1: Download plugin */}
        <div className='onboardingInstallPlugin__step'>
          <div className='onboardingInstallPlugin__step-number'>1</div>
          <div className='onboardingInstallPlugin__step-content'>
            <h3 className='onboardingInstallPlugin__step-title'>
              Download the plugin
            </h3>
            <p className='onboardingInstallPlugin__step-description'>
              Download the ADUP plugin ZIP file to your computer.
            </p>
            <button
              type='button'
              className='onboardingInstallPlugin__download-btn'
              onClick={() => {
                window.open(
                  'https://drive.google.com/file/d/1x5wksWIagulHzrIIdwzNbmR7b-JhdmO2/view?usp=sharing',
                  '_blank'
                );
              }}
            >
              <svg
                width='18'
                height='18'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <polyline
                  points='7 10 12 15 17 10'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <line
                  x1='12'
                  y1='15'
                  x2='12'
                  y2='3'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              Download Plugin (ZIP)
            </button>
          </div>
        </div>

        {/* Step 2: Add plugin in Claude */}
        <div className='onboardingInstallPlugin__step'>
          <div className='onboardingInstallPlugin__step-number'>2</div>
          <div className='onboardingInstallPlugin__step-content'>
            <h3 className='onboardingInstallPlugin__step-title'>
              Add the plugin to Claude
            </h3>
            <p className='onboardingInstallPlugin__step-description'>
              Open{' '}
              <a
                href='https://claude.ai'
                target='_blank'
                rel='noopener noreferrer'
              >
                Claude Cowork
              </a>
              , go to{' '}
              <strong>Customize</strong> &rarr;{' '}
              <strong>+ Add plugin</strong> and upload the downloaded ZIP file.
            </p>
          </div>
        </div>

        {/* Step 3: Copy API key */}
        <div
          className={`onboardingInstallPlugin__step ${
            apiKey ? 'onboardingInstallPlugin__step--completed' : ''
          }`}
        >
          <div className='onboardingInstallPlugin__step-number'>
            {apiKey ? (
              <svg
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <path
                  d='M13.3 4.3L6 11.6L2.7 8.3'
                  stroke='#10b981'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              '3'
            )}
          </div>
          <div className='onboardingInstallPlugin__step-content'>
            <h3 className='onboardingInstallPlugin__step-title'>
              Copy your API key
            </h3>
            <p className='onboardingInstallPlugin__step-description'>
              You'll need this key when setting up the plugin in the next step.
            </p>
            <div className='onboardingInstallPlugin__api-key'>
              {isGenerating ? (
                <div className='onboardingInstallPlugin__api-key-loading'>
                  Generating key...
                </div>
              ) : apiKey ? (
                <>
                  <code className='onboardingInstallPlugin__api-key-value'>
                    {apiKey}
                  </code>
                  <button
                    type='button'
                    className={`onboardingInstallPlugin__api-key-copy ${
                      isCopied
                        ? 'onboardingInstallPlugin__api-key-copy--copied'
                        : ''
                    }`}
                    onClick={handleCopy}
                  >
                    {isCopied ? 'Copied!' : 'Copy'}
                  </button>
                </>
              ) : (
                <div className='onboardingInstallPlugin__api-key-error'>
                  Could not generate key.{' '}
                  <button type='button' onClick={generateApiKey}>
                    Try again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Step 4: Run setup command */}
        <div className='onboardingInstallPlugin__step'>
          <div className='onboardingInstallPlugin__step-number'>4</div>
          <div className='onboardingInstallPlugin__step-content'>
            <h3 className='onboardingInstallPlugin__step-title'>
              Set up the plugin
            </h3>
            <p className='onboardingInstallPlugin__step-description'>
              In the Claude chat, type{' '}
              <code className='onboardingInstallPlugin__inline-code'>
                /adup:setup
              </code>{' '}
              and follow the instructions to enter your API key.
            </p>
          </div>
        </div>

        {/* Step 5: Verify connection */}
        <div
          className={`onboardingInstallPlugin__step ${
            isVerified ? 'onboardingInstallPlugin__step--completed' : ''
          }`}
        >
          <div className='onboardingInstallPlugin__step-number'>
            {isVerified ? (
              <svg
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <path
                  d='M13.3 4.3L6 11.6L2.7 8.3'
                  stroke='#10b981'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              '5'
            )}
          </div>
          <div className='onboardingInstallPlugin__step-content'>
            <h3 className='onboardingInstallPlugin__step-title'>
              Verify your connection
            </h3>
            <p className='onboardingInstallPlugin__step-description'>
              Type{' '}
              <code className='onboardingInstallPlugin__inline-code'>
                /adup:connect
              </code>{' '}
              in Claude to confirm everything is connected.
            </p>
            {isVerified && (
              <div className='onboardingInstallPlugin__verified'>
                Plugin connected successfully!
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Help button */}
      <div className='onboardingInstallPlugin__help'>
        <svg
          width='16'
          height='16'
          viewBox='0 0 24 24'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <circle cx='12' cy='12' r='10' stroke='#6b7280' strokeWidth='2' />
          <path
            d='M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3'
            stroke='#6b7280'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <line
            x1='12'
            y1='17'
            x2='12.01'
            y2='17'
            stroke='#6b7280'
            strokeWidth='2'
            strokeLinecap='round'
          />
        </svg>
        <span>
          Need help?{' '}
          <a
            href='mailto:tara@adup.io?subject=Plugin%20Setup%20Help'
            target='_blank'
            rel='noopener noreferrer'
          >
            Contact our team
          </a>
        </span>
      </div>

      <div className='onboardingInstallPlugin__footer'>
        <MainSquareButton
          type='button'
          value='Continue'
          normal={true}
          onClick={handleContinue}
          style={{
            width: '100%',
            paddingTop: '1.2rem',
            paddingBottom: '1.2rem',
          }}
        />
        <button
          type='button'
          className='onboardingInstallPlugin__skip'
          onClick={handleBackToChoice}
        >
          Go back and choose a different option
        </button>
      </div>

      <OnboardingFooter />
    </div>
  );
};

export default OnboardingInstallPlugin;
